import {
	keepPreviousData,
	MutationFunction,
	useMutation,
	useQuery,
	useQueryClient
} from "@tanstack/react-query";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useTranslation } from "react-i18next";
import {
	deleteProfessionalExperienceAsync,
	getFreelancerCountriesAsync,
	getFreelancerCountryCountsAsync,
	getFreelancerProfile,
	patchFreelancerProfile,
	saveFreelancerOnboardingProgression,
	saveProfessionalExperienceAsync,
	searchFreelancerProfilesAsync,
	updateFreelancerProfileControlledStatus
} from "../api/freelancer-profiles-api";
import { FreelancerProfilePaths } from "../constants/freelancerProfilePaths";
import { useLinearProgressContext } from "../contexts/useLinearProgressContext";
import { useSnackbarContext } from "../contexts/useSnackBarContext";
import { Address } from "../models/Address";
import { CountryCount } from "../models/CountryCount";
import { FreelancerOnboardingFormStep } from "../models/enums/FreelancerOnboardingFormStep";
import { Freelancer } from "../models/Freelancer";
import { FreelancerProfile } from "../models/FreelancerProfile";
import { FreelancerProfileAvailability } from "../models/FreelancerProfileAvailability";
import { PaginatedResponse } from "../models/Pagination";
import { FreelancerProfileSearchRequest } from "../models/requests/FreelancerProfileSearchRequest";
import { UpdateFreelancerProfileBusinessDomainRequest } from "../models/requests/UpdateFreelancerProfileBusinessDomainRequest";
import { UpdateFreelancerProfileCompanyRequest } from "../models/requests/UpdateFreelancerProfileCompanyRequest";
import { UpdateFreelancerProfileCompanyUrlRequest } from "../models/requests/UpdateFreelancerProfileCompanyUrlRequest";
import { UpdateFreelancerProfileDocumentRequest } from "../models/requests/UpdateFreelancerProfileDocumentRequest";
import { UpdateFreelancerProfileLanguageRequest } from "../models/requests/UpdateFreelancerProfileLanguageRequest";
import { UpdateFreelancerProfileProfessionalExperienceRequest } from "../models/requests/UpdateFreelancerProfileProfessionalExperienceRequest";
import { UpdateFreelancerProfileRegionRequest } from "../models/requests/UpdateFreelancerProfileRegionRequest";
import { UpdateFreelancerProfileSectorRequest } from "../models/requests/UpdateFreelancerProfileSectorRequest";
import { UpdateFreelancerProfileSkillRequest } from "../models/requests/UpdateFreelancerProfileSkillRequest";
import { UpdateFreelancerProfileToolRequest } from "../models/requests/UpdateFreelancerProfileToolRequest";
import { UpdateFreelancerProfileUrlRequest } from "../models/requests/UpdateFreelancerProfileUrlRequest";
import JSONPatchOperation from "../utils/JSONPatchOperation";
import JSONPatchOperationType from "../utils/JSONPatchOperationType";
import { formatPlaceToCity } from "../utils/placesUtils";
import { FreelancerOnboardingMyAvailabilityStepViewModel } from "../ViewModels/FreelancerOnboardingMyAvailabilityStepViewModel";
import { FreelancerOnboardingMyExpertiseStepViewModel } from "../ViewModels/FreelancerOnboardingMyExpertiseStepViewModel";
import {
	AddressViewModel,
	FreelancerOnboardingMyPersonalInformationStepViewModel
} from "../ViewModels/FreelancerOnboardingMyPersonalInformationStepViewModel";
import {
	FreelancerOnboardingMyPresentationExperienceViewModel,
	FreelancerOnboardingMyPresentationStepViewModel
} from "../ViewModels/FreelancerOnboardingMyPresentationStepViewModel";
import { FreelancerOnboardingMyProfessionalInformationStepViewModel } from "../ViewModels/FreelancerOnboardingMyProfessionalInformationStepViewModel";
import { FreelancerOnboardingMySectorsStepViewModel } from "../ViewModels/FreelancerOnboardingMySectorsStepViewModel";
import { FreelancerOnboardingMySkillsStepViewModel } from "../ViewModels/FreelancerOnboardingMySkillsStepViewModel";

export interface SaveOnboardingOptions {
	hideAllSnackbars?: boolean;
}

export const useFreelancersProfiles = (
	freelancerProfileSearchRequest: FreelancerProfileSearchRequest
) => {
	return useQuery<PaginatedResponse<FreelancerProfile>, Error>({
		queryKey: ["freelancerProfiles", freelancerProfileSearchRequest],
		queryFn: () => searchFreelancerProfilesAsync(freelancerProfileSearchRequest),
		refetchOnMount: "always",
		refetchOnWindowFocus: false,
		gcTime: 0,
		staleTime: 0
	});
};

export const useFreelancerProfile = (
	freelancerProfileId: number,
	enabled: boolean = true,
	freelancerProfileKey: string = ""
) => {
	return useQuery({
		queryKey: ["freelancerProfile", freelancerProfileId],
		queryFn: () => getFreelancerProfile(freelancerProfileId, freelancerProfileKey),
		enabled:
			(freelancerProfileId > 0 ||
				(freelancerProfileId <= 0 && freelancerProfileKey !== "")) &&
			enabled,
		refetchOnWindowFocus: false,
		// Keep the previously loaded profile while the id transitions (cascade with the
		// freelancer query) so dependent screens never see it flip back to undefined.
		placeholderData: keepPreviousData
	});
};

export const useFreelancerCountries = (onboardingStatus?: string | null) => {
	return useQuery<string[], Error>({
		queryKey: ["freelancerCountries", onboardingStatus],
		queryFn: () => getFreelancerCountriesAsync(onboardingStatus),
		refetchOnWindowFocus: false
	});
};

export const useFreelancerCountryCounts = (onboardingStatus?: string | null) => {
	return useQuery<CountryCount[], Error>({
		queryKey: ["freelancerCountryCounts", onboardingStatus],
		queryFn: () => getFreelancerCountryCountsAsync(onboardingStatus),
		refetchOnWindowFocus: false
	});
};

export function usePatchFreelancerOnboardingFormsProgressMutation<T>(
	mutationFn: MutationFunction<void, T>,
	options: SaveOnboardingOptions = {
		hideAllSnackbars: false
	}
) {
	const queryClient = useQueryClient();
	const { showSuccess, showError, showWarning } = useSnackbarContext();
	const { hideProgress } = useLinearProgressContext();

	return useMutation({
		mutationFn,
		onSuccess: () => {
			hideProgress();

			!options.hideAllSnackbars &&
				showSuccess("freelancerOnboardingFormsProgressUpdate.updatedSuccess");

			queryClient.invalidateQueries({
				queryKey: ["currentUser"]
			});

			queryClient.invalidateQueries({
				queryKey: ["freelancer"]
			});

			queryClient.invalidateQueries({
				queryKey: ["currentFreelancer"]
			});

			queryClient.invalidateQueries({
				queryKey: ["freelancerProfile"]
			});
		},
		onError: (error: Error) => {
			hideProgress();
			console.error(error);

			if (error.message.includes("document")) {
				!options.hideAllSnackbars &&
					showWarning("freelanceProfileProfessionalExperience.updateWarning", 9999999);
				return;
			}

			!options.hideAllSnackbars &&
				showError("freelancerOnboardingFormsProgressUpdate.updateError");
		}
	});
}

export const usePatchMyProfileFreelancerOnboardingStep = (
	options: SaveOnboardingOptions = {
		hideAllSnackbars: false
	}
) => {
	const { showProgress } = useLinearProgressContext();

	return usePatchFreelancerOnboardingFormsProgressMutation(
		({
			freelancerProfile,
			newStep
		}: {
			freelancerProfile: FreelancerProfile;
			newStep: FreelancerOnboardingFormStep;
		}) => {
			showProgress();

			return patchFreelancerProfile(
				freelancerProfile,
				setOperationsForMyProfileFreelancerOnboardingStep(newStep)
			);
		},
		options
	);
};

export const usePatchMyExpertiseFreelancerOnboardingStep = (
	options: SaveOnboardingOptions = {
		hideAllSnackbars: false
	}
) => {
	const { showProgress } = useLinearProgressContext();

	return usePatchFreelancerOnboardingFormsProgressMutation(
		({
			freelancerOnboardingMyExpertiseStepViewModel,
			freelancerProfile,
			newStep
		}: {
			freelancerOnboardingMyExpertiseStepViewModel: FreelancerOnboardingMyExpertiseStepViewModel;
			freelancerProfile: FreelancerProfile;
			newStep: FreelancerOnboardingFormStep;
		}) => {
			showProgress();

			return patchFreelancerProfile(
				freelancerProfile,
				setOperationsForMyExpertiseFreelancerOnboardingStep(
					freelancerOnboardingMyExpertiseStepViewModel,
					newStep
				)
			);
		},
		options
	);
};

export const usePatchMySkillsFreelancerOnboardingStep = (
	options: SaveOnboardingOptions = {
		hideAllSnackbars: false
	}
) => {
	const { showProgress } = useLinearProgressContext();

	return usePatchFreelancerOnboardingFormsProgressMutation(
		({
			freelancer,
			freelancerProfile,
			freelancerOnboardingMySkillsStepViewModel,
			newStep
		}: {
			freelancer: Freelancer;
			freelancerProfile: FreelancerProfile;
			freelancerOnboardingMySkillsStepViewModel: FreelancerOnboardingMySkillsStepViewModel;
			newStep: FreelancerOnboardingFormStep;
		}) => {
			showProgress();

			return patchFreelancerProfile(
				freelancerProfile,
				setOperationsForMySkillsFreelancerOnboardingStep(
					freelancer,
					freelancerProfile,
					freelancerOnboardingMySkillsStepViewModel,
					newStep
				)
			);
		},
		options
	);
};

export const usePatchMySectorsFreelancerOnboardingStep = (
	options: SaveOnboardingOptions = {
		hideAllSnackbars: false
	}
) => {
	const { showProgress } = useLinearProgressContext();

	return usePatchFreelancerOnboardingFormsProgressMutation(
		({
			freelancerProfile,
			freelancerOnboardingMySectorsStepViewModel,
			newStep
		}: {
			freelancerProfile: FreelancerProfile;
			freelancerOnboardingMySectorsStepViewModel: FreelancerOnboardingMySectorsStepViewModel;
			newStep: FreelancerOnboardingFormStep;
		}) => {
			showProgress();

			return patchFreelancerProfile(
				freelancerProfile,
				setOperationsForMySectorsFreelancerOnboardingStep(
					freelancerProfile,
					freelancerOnboardingMySectorsStepViewModel,
					newStep
				)
			);
		},
		options
	);
};

export const useSaveMyProfileFreelancerOnboardingProgress = (
	options: SaveOnboardingOptions = {
		hideAllSnackbars: false
	}
) => {
	const { showProgress } = useLinearProgressContext();

	return usePatchFreelancerOnboardingFormsProgressMutation(
		({
			freelancerProfile,
			newStep
		}: {
			freelancerProfile: FreelancerProfile;
			newStep: FreelancerOnboardingFormStep;
		}) => {
			showProgress();

			return saveFreelancerOnboardingProgression(
				freelancerProfile,
				setOperationsForMyProfileFreelancerOnboardingStep(newStep)
			);
		},
		options
	);
};

export const useSaveMyExpertiseFreelancerOnboardingProgress = (
	options: SaveOnboardingOptions = {
		hideAllSnackbars: false
	}
) => {
	const { showProgress } = useLinearProgressContext();

	return usePatchFreelancerOnboardingFormsProgressMutation(
		({
			freelancerOnboardingMyExpertiseStepViewModel,
			freelancerProfile,
			newStep
		}: {
			freelancerOnboardingMyExpertiseStepViewModel: FreelancerOnboardingMyExpertiseStepViewModel;
			freelancerProfile: FreelancerProfile;
			newStep: FreelancerOnboardingFormStep;
		}) => {
			showProgress();

			return saveFreelancerOnboardingProgression(
				freelancerProfile,
				setOperationsForMyExpertiseFreelancerOnboardingStep(
					freelancerOnboardingMyExpertiseStepViewModel,
					newStep
				)
			);
		},
		options
	);
};

export const useSaveMySkillsFreelancerOnboardingProgress = (
	options: SaveOnboardingOptions = {
		hideAllSnackbars: false
	}
) => {
	const { showProgress } = useLinearProgressContext();

	return usePatchFreelancerOnboardingFormsProgressMutation(
		({
			freelancer,
			freelancerProfile,
			freelancerOnboardingMySkillsStepViewModel,
			newStep
		}: {
			freelancer: Freelancer;
			freelancerProfile: FreelancerProfile;
			freelancerOnboardingMySkillsStepViewModel: FreelancerOnboardingMySkillsStepViewModel;
			newStep: FreelancerOnboardingFormStep;
		}) => {
			showProgress();

			return saveFreelancerOnboardingProgression(
				freelancerProfile,
				setOperationsForMySkillsFreelancerOnboardingStep(
					freelancer,
					freelancerProfile,
					freelancerOnboardingMySkillsStepViewModel,
					newStep
				)
			);
		},
		options
	);
};

export const useSaveMySectorsFreelancerOnboardingProgress = (
	options: SaveOnboardingOptions = {
		hideAllSnackbars: false
	}
) => {
	const { showProgress } = useLinearProgressContext();

	return usePatchFreelancerOnboardingFormsProgressMutation(
		({
			freelancerProfile,
			freelancerOnboardingMySectorsStepViewModel,
			newStep
		}: {
			freelancerProfile: FreelancerProfile;
			freelancerOnboardingMySectorsStepViewModel: FreelancerOnboardingMySectorsStepViewModel;
			newStep: FreelancerOnboardingFormStep;
		}) => {
			showProgress();

			return saveFreelancerOnboardingProgression(
				freelancerProfile,
				setOperationsForMySectorsFreelancerOnboardingStep(
					freelancerProfile,
					freelancerOnboardingMySectorsStepViewModel,
					newStep
				)
			);
		},
		options
	);
};

export const usePatchMyPresentationFreelancerOnboardingStep = (
	options: SaveOnboardingOptions = {
		hideAllSnackbars: false
	}
) => {
	const { showProgress } = useLinearProgressContext();
	const { setOperationsForMyPresentationFreelancerOnboardingStep } =
		useCreatePresentationOperations();

	return usePatchFreelancerOnboardingFormsProgressMutation(
		async ({
			freelancerOnboardingMyPresentationStepViewModel,
			freelancerProfile,
			newStep
		}: {
			freelancerOnboardingMyPresentationStepViewModel: FreelancerOnboardingMyPresentationStepViewModel;
			freelancerProfile: FreelancerProfile;
			newStep: FreelancerOnboardingFormStep;
		}) => {
			showProgress();

			return patchFreelancerProfile(
				freelancerProfile,
				await setOperationsForMyPresentationFreelancerOnboardingStep(
					freelancerOnboardingMyPresentationStepViewModel,
					newStep
				)
			);
		},
		options
	);
};

export const useSaveMyPresentationFreelancerOnboardingProgress = (
	options: SaveOnboardingOptions = {
		hideAllSnackbars: false
	}
) => {
	const { showProgress } = useLinearProgressContext();
	const { setOperationsForMyPresentationFreelancerOnboardingStep } =
		useCreatePresentationOperations();

	return usePatchFreelancerOnboardingFormsProgressMutation(
		async ({
			freelancerOnboardingMyPresentationStepViewModel,
			freelancerProfile,
			newStep
		}: {
			freelancerOnboardingMyPresentationStepViewModel: FreelancerOnboardingMyPresentationStepViewModel;
			freelancerProfile: FreelancerProfile;
			newStep: FreelancerOnboardingFormStep;
		}) => {
			showProgress();

			return saveFreelancerOnboardingProgression(
				freelancerProfile,
				await setOperationsForMyPresentationFreelancerOnboardingStep(
					freelancerOnboardingMyPresentationStepViewModel,
					newStep
				)
			);
		},
		options
	);
};

export const usePutFreelanceProfileProfessionalExperience = () => {
	const { showProgress } = useLinearProgressContext();
	const { showSuccess, showError, showWarning } = useSnackbarContext();
	const { hideProgress } = useLinearProgressContext();

	return useMutation({
		mutationFn: ({
			updateFreelancerProfileProfessionalExperienceRequest,
			freelancerProfile,
			languageCode
		}: {
			updateFreelancerProfileProfessionalExperienceRequest: UpdateFreelancerProfileProfessionalExperienceRequest;
			freelancerProfile: FreelancerProfile;
			/**
			 * Language of the payload's translatable content. Set for a CREATE authored in a
			 * non-primary language: the API then keeps the primary columns empty and stores the
			 * values as human cells of that language (a bare create would 400 on the required
			 * translatable fields, which are empty in the primary payload in that case).
			 */
			languageCode?: string;
		}) => {
			showProgress();

			return saveProfessionalExperienceAsync(
				freelancerProfile.id,
				updateFreelancerProfileProfessionalExperienceRequest,
				languageCode
			);
		},
		onSuccess: () => {
			hideProgress();
			showSuccess("freelanceProfileProfessionalExperience.updatedSuccess");
		},
		onError: (error: Error) => {
			hideProgress();
			console.error(error);

			if (error.message.includes("document")) {
				showWarning("freelanceProfileProfessionalExperience.updateWarning", 9999999);
				return;
			}

			showError("freelanceProfileProfessionalExperience.updateError");
		}
	});
};

export const useDeleteFreelanceProfileProfessionalExperience = () => {
	const { showProgress } = useLinearProgressContext();

	const queryClient = useQueryClient();
	const { showSuccess, showError } = useSnackbarContext();
	const { hideProgress } = useLinearProgressContext();

	return useMutation({
		mutationFn: ({
			freelancerOnboardingMyPresentationExperienceViewModel,
			freelancerProfile
		}: {
			freelancerOnboardingMyPresentationExperienceViewModel: FreelancerOnboardingMyPresentationExperienceViewModel;
			freelancerProfile: FreelancerProfile;
		}) => {
			showProgress();

			return deleteProfessionalExperienceAsync(
				freelancerProfile.id,
				freelancerOnboardingMyPresentationExperienceViewModel.id!
			);
		},
		onSuccess: () => {
			hideProgress();
			showSuccess("freelanceProfileProfessionalExperience.deleteSuccess");

			queryClient.invalidateQueries({
				queryKey: ["freelancerProfile"]
			});
		},
		onError: (error: Error) => {
			hideProgress();
			console.error(error);
			showError("freelanceProfileProfessionalExperience.deleteError");
		}
	});
};

export const usePatchMyAvailabilityFreelancerOnboardingStep = (
	options: SaveOnboardingOptions = {
		hideAllSnackbars: false
	}
) => {
	const { showProgress } = useLinearProgressContext();

	return usePatchFreelancerOnboardingFormsProgressMutation(
		({
			freelancerOnboardingMyAvailabilityStepViewModel,
			freelancerProfile,
			newStep
		}: {
			freelancerOnboardingMyAvailabilityStepViewModel: FreelancerOnboardingMyAvailabilityStepViewModel;
			freelancerProfile: FreelancerProfile;
			newStep: FreelancerOnboardingFormStep;
		}) => {
			showProgress();

			return patchFreelancerProfile(
				freelancerProfile,
				setOperationsForMyAvailabilityFreelancerOnboardingStep(
					freelancerProfile,
					freelancerOnboardingMyAvailabilityStepViewModel,
					newStep
				)
			);
		},
		options
	);
};

export const useSaveMyAvailabilityFreelancerOnboardingProgress = (
	options: SaveOnboardingOptions = {
		hideAllSnackbars: false
	}
) => {
	const { showProgress } = useLinearProgressContext();

	return usePatchFreelancerOnboardingFormsProgressMutation(
		({
			freelancerOnboardingMyAvailabilityStepViewModel,
			freelancerProfile,
			newStep
		}: {
			freelancerOnboardingMyAvailabilityStepViewModel: FreelancerOnboardingMyAvailabilityStepViewModel;
			freelancerProfile: FreelancerProfile;
			newStep: FreelancerOnboardingFormStep;
		}) => {
			showProgress();

			return saveFreelancerOnboardingProgression(
				freelancerProfile,
				setOperationsForMyAvailabilityFreelancerOnboardingStep(
					freelancerProfile,
					freelancerOnboardingMyAvailabilityStepViewModel,
					newStep
				)
			);
		},
		options
	);
};

export const usePatchMyProfessionalInformationFreelancerOnboardingStep = (
	options: SaveOnboardingOptions = {
		hideAllSnackbars: false
	}
) => {
	const { showProgress } = useLinearProgressContext();
	const { setOperationsForMyProfessionalInformationFreelancerOnboardingStep } =
		useCreateProfessionalInformationOperations();

	return usePatchFreelancerOnboardingFormsProgressMutation(
		async ({
			freelancerOnboardingMyProfessionalInformationStepViewModel,
			freelancerProfile,
			newStep
		}: {
			freelancerOnboardingMyProfessionalInformationStepViewModel: FreelancerOnboardingMyProfessionalInformationStepViewModel;
			freelancerProfile: FreelancerProfile;
			newStep: FreelancerOnboardingFormStep;
		}) => {
			showProgress();

			return patchFreelancerProfile(
				freelancerProfile,
				await setOperationsForMyProfessionalInformationFreelancerOnboardingStep(
					freelancerProfile,
					freelancerOnboardingMyProfessionalInformationStepViewModel,
					newStep
				)
			);
		},
		options
	);
};

export const useSaveMyProfessionalInformationFreelancerOnboardingProgress = (
	options: SaveOnboardingOptions = {
		hideAllSnackbars: false
	}
) => {
	const { showProgress } = useLinearProgressContext();
	const { setOperationsForMyProfessionalInformationFreelancerOnboardingStep } =
		useCreateProfessionalInformationOperations();

	return usePatchFreelancerOnboardingFormsProgressMutation(
		async ({
			freelancerOnboardingMyProfessionalInformationStepViewModel,
			freelancerProfile,
			newStep
		}: {
			freelancerOnboardingMyProfessionalInformationStepViewModel: FreelancerOnboardingMyProfessionalInformationStepViewModel;
			freelancerProfile: FreelancerProfile;
			newStep: FreelancerOnboardingFormStep;
		}) => {
			showProgress();

			return saveFreelancerOnboardingProgression(
				freelancerProfile,
				await setOperationsForMyProfessionalInformationFreelancerOnboardingStep(
					freelancerProfile,
					freelancerOnboardingMyProfessionalInformationStepViewModel,
					newStep
				)
			);
		},
		options
	);
};

export const usePatchMyPersonalInformationFreelancerOnboardingStep = (
	options: SaveOnboardingOptions = {
		hideAllSnackbars: false
	}
) => {
	const { showProgress } = useLinearProgressContext();
	const { setOperationsForMyPersonalInformationFreelancerOnboardingStep } =
		useCreatePersonalInformationOperations();

	return usePatchFreelancerOnboardingFormsProgressMutation(
		async ({
			freelancerOnboardingMyPersonalInformationStepViewModel,
			freelancer,
			newStep
		}: {
			freelancerOnboardingMyPersonalInformationStepViewModel: FreelancerOnboardingMyPersonalInformationStepViewModel;
			freelancer: Freelancer;
			newStep: FreelancerOnboardingFormStep;
		}) => {
			showProgress();

			return patchFreelancerProfile(
				freelancer.profile,
				await setOperationsForMyPersonalInformationFreelancerOnboardingStep(
					freelancerOnboardingMyPersonalInformationStepViewModel,
					newStep,
					freelancer
				)
			);
		},
		options
	);
};

export const useSaveMyPersonalInformationFreelancerOnboardingProgress = (
	options: SaveOnboardingOptions = {
		hideAllSnackbars: false
	}
) => {
	const { showProgress } = useLinearProgressContext();
	const { setOperationsForMyPersonalInformationFreelancerOnboardingStep } =
		useCreatePersonalInformationOperations();

	return usePatchFreelancerOnboardingFormsProgressMutation(
		async ({
			freelancerOnboardingMyPersonalInformationStepViewModel,
			freelancer,
			newStep
		}: {
			freelancerOnboardingMyPersonalInformationStepViewModel: FreelancerOnboardingMyPersonalInformationStepViewModel;
			freelancer: Freelancer;
			newStep: FreelancerOnboardingFormStep;
		}) => {
			showProgress();

			return saveFreelancerOnboardingProgression(
				freelancer.profile,
				await setOperationsForMyPersonalInformationFreelancerOnboardingStep(
					freelancerOnboardingMyPersonalInformationStepViewModel,
					newStep,
					freelancer
				)
			);
		},
		options
	);
};

const setOperationsForMyProfileFreelancerOnboardingStep = (
	newStep: FreelancerOnboardingFormStep
) => {
	return [
		{
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.CURRENT_ONBOARDING_FORM_STEP,
			value: newStep
		}
	] as JSONPatchOperation[];
};

const setOperationsForMyExpertiseFreelancerOnboardingStep = (
	freelancerOnboardingMyExpertiseStepViewModel: FreelancerOnboardingMyExpertiseStepViewModel,
	newStep: FreelancerOnboardingFormStep
) => {
	// `title` is translatable and saved per-language (including the primary FR) through the
	// translatable-form save path — exactly like the other languages — never through this step
	// patch, so re-saving a step can never overwrite the worker-projected primary column.
	const jSONPatchOperations = [
		{
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.CURRENT_ONBOARDING_FORM_STEP,
			value: newStep
		},
		{
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.EXPERTISE_ID,
			value: freelancerOnboardingMyExpertiseStepViewModel.expertise?.id
		},
		{
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.EXPERIENCE_ID,
			value: freelancerOnboardingMyExpertiseStepViewModel.experience?.id
		},
		{
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.FREELANCING_YEARS_OF_EXPERIENCE_ID,
			value: freelancerOnboardingMyExpertiseStepViewModel.freelancingYearsOfExperience?.id
		}
	] as JSONPatchOperation[];

	return jSONPatchOperations;
};

const setOperationsForMySkillsFreelancerOnboardingStep = (
	freelancer: Freelancer,
	freelancerProfile: FreelancerProfile,
	freelancerOnboardingMySkillsStepViewModel: FreelancerOnboardingMySkillsStepViewModel,
	newStep: FreelancerOnboardingFormStep
) => {
	return [
		{
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.CURRENT_ONBOARDING_FORM_STEP,
			value: newStep
		},
		{
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.SKILLS,
			value: freelancerOnboardingMySkillsStepViewModel.dataProcessStageViewModels.flatMap(
				x =>
					x.skillViewModels.map(
						x =>
							({
								id: x.id,
								freelancerProfileId: freelancerProfile.id,
								dataProcessStageId: x.dataProcessStage.id,
								skillId: x.skill.id,
								level: x.level
							} as UpdateFreelancerProfileSkillRequest)
					)
			)
		},
		{
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.TOOLS,
			value: freelancerOnboardingMySkillsStepViewModel.dataProcessStageViewModels.flatMap(
				x =>
					x.toolViewModels.map(
						x =>
							({
								id: x.id,
								freelancerProfileId: freelancerProfile.id,
								dataProcessStageId: x.dataProcessStage.id,
								toolId: x.tool.id,
								level: x.level
							} as UpdateFreelancerProfileToolRequest)
					)
			)
		},
		{
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.FREELANCER_LANGUAGES,
			value: freelancerOnboardingMySkillsStepViewModel.languageViewModels.map(
				x =>
					({
						id: x.id,
						freelancerId: freelancer.id,
						languageId: x.language.id,
						languageProficiency: x.languageProficiency
					} as UpdateFreelancerProfileLanguageRequest)
			)
		},
		{
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.REGIONS,
			value: freelancerOnboardingMySkillsStepViewModel.regionViewModels.map(
				x =>
					({
						id: x.id,
						freelancerProfileId: freelancerProfile.id,
						regionId: x.region.id
					} as UpdateFreelancerProfileRegionRequest)
			)
		}
	] as JSONPatchOperation[];
};

const setOperationsForMySectorsFreelancerOnboardingStep = (
	freelancerProfile: FreelancerProfile,
	freelancerOnboardingMySectorsStepViewModel: FreelancerOnboardingMySectorsStepViewModel,
	newStep: FreelancerOnboardingFormStep
) => {
	return [
		{
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.CURRENT_ONBOARDING_FORM_STEP,
			value: newStep
		},
		{
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.SECTORS,
			value: freelancerOnboardingMySectorsStepViewModel.sectorViewModels.map(
				x =>
					({
						id: x.id,
						freelancerProfileId: freelancerProfile.id,
						sectorId: x.sector.id
					} as UpdateFreelancerProfileSectorRequest)
			)
		},
		{
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.BUSINESS_DOMAINS,
			value: freelancerOnboardingMySectorsStepViewModel.businessDomainViewModels.map(
				x =>
					({
						id: x.id,
						freelancerProfileId: freelancerProfile.id,
						businessDomainId: x.businessDomain.id
					} as UpdateFreelancerProfileBusinessDomainRequest)
			)
		}
	] as JSONPatchOperation[];
};

// Helper hooks for operations that need Google Maps translation
const useCreatePresentationOperations = () => {
	const placesLib = useMapsLibrary("places");

	const setOperationsForMyPresentationFreelancerOnboardingStep = async (
		freelancerOnboardingMyPresentationStepViewModel: FreelancerOnboardingMyPresentationStepViewModel,
		newStep: FreelancerOnboardingFormStep
	) => {
		// `presentation` is translatable and saved per-language (including the primary FR) through
		// the translatable-form save path — like the other languages — never through this step
		// patch, so re-saving a step can never overwrite the worker-projected primary column.
		// Translate professional experiences cities to English
		const translatedProfessionalExperiences = await Promise.all(
			freelancerOnboardingMyPresentationStepViewModel.professionalExperiences.map(
				async ProfessionalExperience => {
					let englishCityName = ProfessionalExperience.city?.label ?? "";
					let englishCityId = ProfessionalExperience.city?.id ?? "";

					// If we have a city with place_id, translate it to English
					if (ProfessionalExperience.city?.id && placesLib) {
						const englishPlace = await translatePlaceToEnglish(
							placesLib,
							ProfessionalExperience.city.id
						);
						if (englishPlace) {
							englishCityName = formatPlaceToCity(englishPlace);
							englishCityId = englishPlace.place_id || ProfessionalExperience.city.id;
						}
					}

					return {
						id: ProfessionalExperience.id,
						freelancerProfileId: ProfessionalExperience.freelancerProfileId,
						isTopExperience: ProfessionalExperience.isTopExperience,
						cityId: englishCityId,
						cityName: englishCityName,
						companyName: ProfessionalExperience.companyName,
						sector: ProfessionalExperience.sector,
						jobTitle: ProfessionalExperience.jobTitle,
						jobType: ProfessionalExperience.jobType,
						startDate: ProfessionalExperience.startDate,
						endDate: ProfessionalExperience.endDate,
						isCurrentPosition: ProfessionalExperience.isCurrentPosition,
						jobLocation: ProfessionalExperience.jobLocation,
						missionDescription: ProfessionalExperience.missionDescription,
						results: ProfessionalExperience.results,
						technicalStack: ProfessionalExperience.technicalStack,
						skills: ProfessionalExperience.skills.map(ProfessionalExperienceSkill => ({
							id: ProfessionalExperienceSkill.id ?? 0,
							skillId: ProfessionalExperienceSkill.skill.id,
							freelancerProfileProfessionalExperienceId: ProfessionalExperience.id
						})),
						businessDomains: ProfessionalExperience.businessDomains.map(
							ProfessionalExperienceBusinessDomain => ({
								id: ProfessionalExperienceBusinessDomain.id ?? 0,
								businessDomainId: ProfessionalExperienceBusinessDomain.businessDomain.id,
								freelancerProfileProfessionalExperienceId: ProfessionalExperience.id
							})
						),
						documents:
							ProfessionalExperience.documents.length > 0
								? ProfessionalExperience.documents.map(document => ({
										...document,
										urlPath: ""
								  }))
								: [],
						urls: ProfessionalExperience.urls
					} as UpdateFreelancerProfileProfessionalExperienceRequest;
				}
			)
		);

		const jSONPatchOperationType: JSONPatchOperation[] = [
			{
				op: JSONPatchOperationType.Replace,
				path: FreelancerProfilePaths.CURRENT_ONBOARDING_FORM_STEP,
				value: newStep
			},
			{
				op: JSONPatchOperationType.Replace,
				path: FreelancerProfilePaths.IS_TRAINER,
				value: freelancerOnboardingMyPresentationStepViewModel.isTrainer
			},
			{
				op: JSONPatchOperationType.Replace,
				path: FreelancerProfilePaths.PROFESSIONAL_EXPERIENCES,
				value: translatedProfessionalExperiences
			}
		];

		return jSONPatchOperationType;
	};

	return {
		setOperationsForMyPresentationFreelancerOnboardingStep
	};
};

const setOperationsForMyAvailabilityFreelancerOnboardingStep = (
	freelancerProfile: FreelancerProfile,
	freelancerOnboardingMyAvailabilityStepViewModel: FreelancerOnboardingMyAvailabilityStepViewModel,
	newStep: FreelancerOnboardingFormStep
): JSONPatchOperation[] => {
	const isFreelancerProfileOnMission = (
		freelancerOnboardingMyAvailabilityStepViewModel: FreelancerOnboardingMyAvailabilityStepViewModel
	): false | null => {
		return freelancerOnboardingMyAvailabilityStepViewModel.selectedAvailability ===
			"onMission"
			? false
			: null;
	};

	const isFreelancerProfileWouldWorkPartTime = (
		freelancerOnboardingMyAvailabilityStepViewModel: FreelancerOnboardingMyAvailabilityStepViewModel
	): false | null => {
		return freelancerOnboardingMyAvailabilityStepViewModel.selectedTimeCommitment ===
			"partTime"
			? false
			: null;
	};

	const jSONPatchOperationType: JSONPatchOperation[] = [
		{
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.CURRENT_ONBOARDING_FORM_STEP,
			value: newStep
		},
		{
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.AVAILABILITY,
			value: {
				id: freelancerProfile.availability?.id ?? 0,
				freelancerProfileId: freelancerProfile.id,
				isAvailable:
					freelancerOnboardingMyAvailabilityStepViewModel.selectedAvailability ===
					"available"
						? true
						: isFreelancerProfileOnMission(
								freelancerOnboardingMyAvailabilityStepViewModel
						  ),
				missionEndDate:
					freelancerOnboardingMyAvailabilityStepViewModel.missionEndDate?.format(
						"YYYY-MM-DD"
					),
				isFullTime:
					freelancerOnboardingMyAvailabilityStepViewModel.selectedTimeCommitment ===
					"fullTime"
						? true
						: isFreelancerProfileWouldWorkPartTime(
								freelancerOnboardingMyAvailabilityStepViewModel
						  ),
				daysPerWeek: freelancerOnboardingMyAvailabilityStepViewModel.daysPerWeek
			} as FreelancerProfileAvailability
		}
	];

	return jSONPatchOperationType;
};

// Utility functions for translating geographic data to English
const translatePlaceToEnglish = async (
	placesLib: google.maps.PlacesLibrary | null,
	placeId: string,
	fields: string[] = ["address_components", "formatted_address", "name"]
): Promise<google.maps.places.PlaceResult | null> => {
	if (!placesLib || !placeId) return null;

	return new Promise(resolve => {
		const dummyDiv = document.createElement("div");
		const placesService = new placesLib.PlacesService(dummyDiv);

		placesService.getDetails(
			{
				placeId: placeId,
				fields: fields,
				language: "en" // Force English language
			},
			(place, status) => {
				if (status === google.maps.places.PlacesServiceStatus.OK && place) {
					resolve(place);
				} else {
					resolve(null);
				}
			}
		);
	});
};

// Generic helper function to translate address data
const translateAddressData = async (
	placesLib: google.maps.PlacesLibrary | null,
	selectedAddress: AddressViewModel,
	idSource: number | undefined
): Promise<Address | null> => {
	if (!selectedAddress) return null;

	let englishCity = selectedAddress.city || "";
	let englishCountry = selectedAddress.country || "";
	let englishFullAddress = selectedAddress.fullAddress || "";
	let englishFormattedAddress = selectedAddress.formattedAddress || "";

	if (selectedAddress.placeId && placesLib) {
		const englishAddressPlace = await translatePlaceToEnglish(
			placesLib,
			selectedAddress.placeId
		);
		if (englishAddressPlace) {
			englishCity = formatPlaceToCity(englishAddressPlace);
			englishCountry = extractEnglishCountryName(englishAddressPlace);
			englishFullAddress = englishAddressPlace.formatted_address || englishFullAddress;
			englishFormattedAddress =
				englishAddressPlace.formatted_address || englishFormattedAddress;
		}
	}

	return {
		id: idSource || 0,
		street: selectedAddress.street || "",
		city: englishCity,
		postalCode: selectedAddress.postalCode || "",
		country: englishCountry,
		placeId: selectedAddress.placeId || "",
		latitude: selectedAddress.latitude || 0,
		longitude: selectedAddress.longitude || 0,
		fullAddress: englishFullAddress,
		countryCode: selectedAddress?.countryCode || "",
		region: selectedAddress.region || "",
		formattedAddress: englishFormattedAddress,
		streetNumber: selectedAddress.streetNumber || ""
	} as Address;
};

const extractEnglishCountryName = (
	place: google.maps.places.PlaceResult | null
): string => {
	if (!place?.address_components) return "";

	// Look for country in address components
	const countryComponent = place.address_components.find(component =>
		component.types.includes("country")
	);

	return countryComponent?.long_name || "";
};

const useCreateProfessionalInformationOperations = () => {
	const placesLib = useMapsLibrary("places");

	// Helper function to translate country data
	const translateCountryData = async (
		selectedCountry: google.maps.places.PlaceResult | null
	) => {
		let englishCountryName = selectedCountry?.formatted_address || "";
		let englishCountryId = selectedCountry?.place_id || "";

		if (selectedCountry?.place_id && placesLib) {
			const englishCountryPlace = await translatePlaceToEnglish(
				placesLib,
				selectedCountry.place_id
			);
			if (englishCountryPlace) {
				englishCountryName =
					extractEnglishCountryName(englishCountryPlace) ||
					englishCountryPlace.formatted_address ||
					"";
				englishCountryId = englishCountryPlace.place_id || selectedCountry.place_id;
			}
		}

		return { englishCountryName, englishCountryId };
	};

	// Helper function to translate address data for company
	const translateCompanyAddressData = async (
		selectedAddress: AddressViewModel,
		freelancerProfile: FreelancerProfile
	) => {
		return translateAddressData(
			placesLib,
			selectedAddress,
			freelancerProfile.company?.address?.id
		);
	};

	// Helper function to create company data
	const createCompanyData = async (
		freelancerProfile: FreelancerProfile,
		statusData: any,
		selectedStatus: string
	) => {
		const { englishCountryName, englishCountryId } = await translateCountryData(
			statusData?.selectedCountry
		);
		const translatedAddress = await translateCompanyAddressData(
			statusData?.selectedAddress,
			freelancerProfile
		);

		return {
			id: freelancerProfile.company?.id ?? 0,
			countryId: englishCountryId,
			country: englishCountryName,
			legalName: statusData?.companyName || "",
			identificationNumber: statusData?.identificationNumber || "",
			addressId: translatedAddress
				? freelancerProfile.company?.address?.id ?? 0
				: freelancerProfile.company?.address?.id,
			address: translatedAddress,
			vatNumber: statusData?.vatNumber || "",
			contactFirstName: statusData?.firstName || "",
			contactLastName: statusData?.lastName || "",
			contactEmail: statusData?.email || "",
			contactPhoneNumber:
				statusData?.phoneNumber?.startsWith("+") && statusData.phoneNumber.length <= 5
					? ""
					: statusData?.phoneNumber ?? "",
			isPortageSalarial: selectedStatus === "portage",
			freelancerProfileId: freelancerProfile.id,
			bankAccountId: null,
			bankAccount: null,
			areCompanyInformationAcknowledged:
				statusData?.areCompanyInformationAcknowledged === true,
			isRegistrationInProgress:
				statusData?.isRegistrationInProgress === true,
			urls:
				statusData?.companyUrls?.map(
					(url: any) =>
						({
							id: url.id || 0,
							url: url.url,
							type: url.type?.label,
							freelancerProfileCompanyId: url.freelancerProfileCompanyId || 0
						} as UpdateFreelancerProfileCompanyUrlRequest)
				) || []
		} as UpdateFreelancerProfileCompanyRequest;
	};

	const setOperationsForMyProfessionalInformationFreelancerOnboardingStep = async (
		freelancerProfile: FreelancerProfile,
		freelancerOnboardingMyProfessionalInformationStepViewModel: FreelancerOnboardingMyProfessionalInformationStepViewModel,
		newStep: FreelancerOnboardingFormStep
	): Promise<JSONPatchOperation[]> => {
		let translatedCompanyData = null;

		if (freelancerOnboardingMyProfessionalInformationStepViewModel.selectedStatus) {
			const statusData =
				freelancerOnboardingMyProfessionalInformationStepViewModel.selectedStatus ===
				"portage"
					? freelancerOnboardingMyProfessionalInformationStepViewModel.portageData
					: freelancerOnboardingMyProfessionalInformationStepViewModel.structureData;

			translatedCompanyData = await createCompanyData(
				freelancerProfile,
				statusData,
				freelancerOnboardingMyProfessionalInformationStepViewModel.selectedStatus
			);
		}

		const jSONPatchOperationType: JSONPatchOperation[] = [
			{
				op: JSONPatchOperationType.Replace,
				path: FreelancerProfilePaths.CURRENT_ONBOARDING_FORM_STEP,
				value: newStep
			},
			{
				op: JSONPatchOperationType.Replace,
				path: FreelancerProfilePaths.DAILY_RATE_MIN,
				value: freelancerOnboardingMyProfessionalInformationStepViewModel.dailyRateMin
			},
			{
				op: JSONPatchOperationType.Replace,
				path: FreelancerProfilePaths.DAILY_RATE_MAX,
				value: freelancerOnboardingMyProfessionalInformationStepViewModel.dailyRateMax
			},
			{
				op: JSONPatchOperationType.Replace,
				path: FreelancerProfilePaths.MISSIONS_PREFERRED_DURATION_ID,
				value:
					freelancerOnboardingMyProfessionalInformationStepViewModel
						.preferredMissionDuration?.id
			},
			{
				op: JSONPatchOperationType.Replace,
				path: FreelancerProfilePaths.JOB_PREFERRED_LOCATION_ID,
				value:
					freelancerOnboardingMyProfessionalInformationStepViewModel.preferredLocation?.id
			},
			{
				op: JSONPatchOperationType.Replace,
				path: FreelancerProfilePaths.COMPANY,
				value: translatedCompanyData
			},
			{
				op: JSONPatchOperationType.Replace,
				path: FreelancerProfilePaths.DOCUMENTS,
				value: (
					freelancerOnboardingMyProfessionalInformationStepViewModel.documents || []
				).map(
					doc =>
						({
							id: doc.id || 0,
							name: doc.name,
							urlPath: "",
							type: doc.type?.label,
							content: doc.content || "",
							freelancerProfileId: doc.freelancerProfileId || 0
						} as UpdateFreelancerProfileDocumentRequest)
				)
			},
			{
				op: JSONPatchOperationType.Replace,
				path: FreelancerProfilePaths.URLS,
				value: (
					freelancerOnboardingMyProfessionalInformationStepViewModel.urls || []
				).map(
					url =>
						({
							id: url.id || 0,
							url: url.url,
							type: url.type?.label,
							freelancerProfileId: url.freelancerProfileId || 0
						} as UpdateFreelancerProfileUrlRequest)
				)
			}
		];

		return jSONPatchOperationType;
	};

	return {
		setOperationsForMyProfessionalInformationFreelancerOnboardingStep
	};
};

const useCreatePersonalInformationOperations = () => {
	const placesLib = useMapsLibrary("places");

	// Helper function to translate address data for personal information
	const translatePersonalAddressData = async (
		selectedAddress: AddressViewModel,
		freelancer: Freelancer
	) => {
		return translateAddressData(placesLib, selectedAddress, freelancer.address?.id);
	};

	const setOperationsForMyPersonalInformationFreelancerOnboardingStep = async (
		freelancerOnboardingMyPersonalInformationStepViewModel: FreelancerOnboardingMyPersonalInformationStepViewModel,
		newStep: FreelancerOnboardingFormStep,
		freelancer: Freelancer
	) => {
		const jSONPatchOperations = [
			{
				op: JSONPatchOperationType.Replace,
				path: FreelancerProfilePaths.CURRENT_ONBOARDING_FORM_STEP,
				value: newStep
			}
		] as JSONPatchOperation[];

		jSONPatchOperations.push({
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.FREELANCER_USER_FIRST_NAME,
			value: freelancerOnboardingMyPersonalInformationStepViewModel.firstName
		});

		jSONPatchOperations.push({
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.FREELANCER_USER_LAST_NAME,
			value: freelancerOnboardingMyPersonalInformationStepViewModel.lastName
		});

		jSONPatchOperations.push({
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.FREELANCER_PHONE_NUMBER,
			value:
				freelancerOnboardingMyPersonalInformationStepViewModel.phoneNumber?.startsWith(
					"+"
				) &&
				freelancerOnboardingMyPersonalInformationStepViewModel.phoneNumber.length <= 5
					? ""
					: freelancerOnboardingMyPersonalInformationStepViewModel.phoneNumber ?? ""
		});

		const translatedAddress = await translatePersonalAddressData(
			freelancerOnboardingMyPersonalInformationStepViewModel.selectedAddress!,
			freelancer
		);
		jSONPatchOperations.push({
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.FREELANCER_ADDRESS,
			value: translatedAddress
		});

		jSONPatchOperations.push({
			op: JSONPatchOperationType.Replace,
			path: FreelancerProfilePaths.FREELANCER_PICTURE,
			value: freelancerOnboardingMyPersonalInformationStepViewModel.profilePhoto
		});

		return jSONPatchOperations;
	};

	return {
		setOperationsForMyPersonalInformationFreelancerOnboardingStep
	};
};

/**
 * Hook to update a freelancer profile's "Contrôlé" status (TAM only). The flag
 * lives on the profile (vetted & matchable), so the id passed here is the
 * freelancer profile id.
 */
export const useUpdateFreelancerProfileControlledStatus = () => {
	const { showSuccess, showError } = useSnackbarContext();
	const { showProgress, hideProgress } = useLinearProgressContext();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: async ({
			freelancerProfileId,
			newIsControlled
		}: {
			freelancerProfileId: number;
			newIsControlled: boolean;
		}) => {
			showProgress();
			await updateFreelancerProfileControlledStatus(
				freelancerProfileId,
				newIsControlled
			);
		},
		onSuccess: () => {
			hideProgress();
			showSuccess(t("freelancerOnboarding.controlledStatusUpdated"));
		},
		onError: error => {
			hideProgress();
			console.error("Error updating freelancer profile controlled status:", error);
			showError(t("freelancerOnboarding.controlledStatusUpdateError"));
		}
	});
};
